/* eslint-disable no-param-reassign */
/* eslint-disable max-classes-per-file */
import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { ChipGroupBaseComponent } from '../chip-group-base/chip-group-base.component';
import { ChipOptionComponent, ChipSelectionChange } from './chip-option/chip-option.component';
import { ChipBaseComponent, ChipEvent } from '../chip-base/chip-base.component';
import { ChipActionDirective } from '../../directives/chip-action.directive';
import { ChipGroupVariant } from '../../types/chip-option-group.type';

/** Change event object that is emitted when the chip option group value has changed. */
export class ChipOptionGroupChangeEvent {
  constructor(
    /** Chip option group that emitted the event. */
    public source: ChipOptionGroupComponent,
    /** Value of the chip option group when the event was emitted. */
    public value: any,
  ) {}
}

/**
 * An extension of the ChipGroupBase component that supports chip selection.
 * Used with ChipOption chips.
 */
@Component({
  selector: 'core-chip-option-group',
  templateUrl: './chip-option-group.component.html',
  styleUrls: ['./chip-option-group.component.scss', '../chip-group-base/chip-group-base.component.scss'],
  host: {
    '[class]': `"chip-option-group"
    + " chip-option-group--"+groupVariant`,
    '[class.chip-option-group--disabled]': 'disabled',
    '[class.chip-option-group--required]': 'required',
    '[attr.role]': 'role',
    '[tabIndex]': '(disabled || empty) ? -1 : tabIndex',
    '[attr.aria-describedby]': 'null',
    '[attr.aria-required]': 'role ? required : null',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.aria-multiselectable]': 'multiple',
    '(focus)': 'focus()',
    '(blur)': 'blur()',
    '(keydown)': 'keydown($event)',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipOptionGroupComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipOptionGroupComponent
  extends ChipGroupBaseComponent
  implements AfterContentInit, OnDestroy, ControlValueAccessor
{
  /**
   * Function when touched. Set as part of ControlValueAccessor implementation.
   */
  onTouched = () => {};

  /**
   * Function when changed. Set as part of ControlValueAccessor implementation.
   */
  onChange: (value: any) => void = () => {};

  protected override defaultRole = 'listbox';

  /**
   * Value that was assigned before the option group was initialized
   */
  private _pendingInitialValue: any;

  /**
   * Whether the user should be allowed to select multiple chips
   */
  @Input({ transform: booleanAttribute })
  get multiple(): boolean {
    return this._multiple;
  }

  set multiple(value: boolean) {
    this._multiple = value;
    this.syncOptionGroupProperties();
  }

  private _multiple: boolean = false;

  @Input()
  groupVariant?: ChipGroupVariant = 'default';

  /**
   * The array of selected chips inside the chip option group
   */
  get selected(): ChipOptionComponent[] | ChipOptionComponent {
    const selectedChips = this.chips.toArray().filter((chip) => chip.selected);

    return this.multiple ? selectedChips : selectedChips[0];
  }

  /**
   * Whether or not this chip option group is selectable.
   *
   * When a chip option group is not selectable, the selected states for all
   * the chips inside the chip option group are always ignored.
   */
  @Input({ transform: booleanAttribute })
  get selectable(): boolean {
    return this._selectable;
  }

  set selectable(value: boolean) {
    this._selectable = value;
    this.syncOptionGroupProperties();
  }

  protected _selectable: boolean = true;

  /**
   * A function to compare the option values with the selected values. The first argument
   * is a value from an option. The second is a value from the selection. A boolean
   * should be returned.
   */
  @Input()
  compareWith: (o1: any, o2: any) => boolean = (o1: any, o2: any) => o1 === o2;

  /**
   * Whether this chip option group is required
   */
  @Input({ transform: booleanAttribute })
  required: boolean = false;

  /**
   * Combined stream of all of the child chips' selection change events
   */
  get chipSelectionChanges$(): Observable<ChipSelectionChange> {
    return this.getChipStream<ChipSelectionChange, ChipOptionComponent>((chip) => chip.selectionChange);
  }

  /**
   * Combined stream of all of the child chips' blur events
   */
  get chipBlurChanges$(): Observable<ChipEvent> {
    return this.getChipStream((chip) => chip.onBlur$);
  }

  /**
   * The value of the option group, which is the combined value of the selected chips
   */
  @Input()
  get value(): any {
    return this._value;
  }

  set value(value: any) {
    this.writeValue(value);
    this._value = value;
  }

  protected _value: any;

  /**
   * Event emitted when the selected chip option group value has been changed by the user
   */
  @Output() readonly change: EventEmitter<ChipOptionGroupChangeEvent> = new EventEmitter<ChipOptionGroupChangeEvent>();

  @ContentChildren(ChipOptionComponent, {
    // We need to use `descendants: true`, because Ivy will no longer match
    // indirect descendants if it's left as false.
    descendants: true,
  })
  // We need an initializer here to avoid a TS error. The value will be set in `ngAfterViewInit`.
  override chips: QueryList<ChipOptionComponent> = undefined!;

  ngAfterContentInit(): void {
    if (this._pendingInitialValue !== undefined) {
      Promise.resolve().then(() => {
        this.setSelectionByValue(this._pendingInitialValue, false);
        this._pendingInitialValue = undefined;
      });
    }

    this.chips.changes.pipe(startWith(null), takeUntil(this.destroyed$)).subscribe(() => {
      // Update option group selectable/multiple properties on chips
      this.syncOptionGroupProperties();
    });

    this.chipBlurChanges$.pipe(takeUntil(this.destroyed$)).subscribe(() => this.blur());
    this.chipSelectionChanges$.pipe(takeUntil(this.destroyed$)).subscribe((event) => {
      if (!this.multiple) {
        this.chips.forEach((chip) => {
          if (chip !== event.source) {
            chip.setSelectedState(false, false, false);
          }
        });
      }

      if (event.isUserInput) {
        this.propagateChanges();
      }
    });
  }

  /**
   * Focuses the first selected chip in this chip option group, or the first non-disabled chip when there
   * are no selected chips.
   */
  override focus(): void {
    if (this.disabled) {
      return;
    }

    const firstSelectedChip = this.getFirstSelectedChip();

    if (firstSelectedChip && !firstSelectedChip.disabled) {
      firstSelectedChip.focus();
    } else if (this.chips.length > 0) {
      this.keyManager.setFirstItemActive();
    } else {
      this.elementRef.nativeElement.focus();
    }
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  writeValue(value: any): void {
    if (this.chips) {
      this.setSelectionByValue(value, false);
    } else if (value != null) {
      this._pendingInitialValue = value;
    }
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Selects all chips with value
   */
  setSelectionByValue(value: any, isUserInput: boolean = true) {
    this.clearSelection();

    if (Array.isArray(value)) {
      value.forEach((currentValue) => this._selectValue(currentValue, isUserInput));
    } else {
      this._selectValue(value, isUserInput);
    }
  }

  /**
   * When blurred, marks the field as touched when focus moved outside the chip option group
   */
  blur(): void {
    if (!this.disabled) {
      // Wait to see if focus moves to an individual chip.
      setTimeout(() => {
        if (!this.focused) {
          this.markAsTouched();
        }
      });
    }
  }

  keydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      super.allowFocusEscape();
    }
  }

  /**
   * Marks the field as touched
   */
  private markAsTouched(): void {
    this.onTouched();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Emits change event to set the model value
   */
  private propagateChanges(): void {
    let valueToEmit: any = null;

    if (Array.isArray(this.selected)) {
      valueToEmit = this.selected.map((chip) => chip.value);
    } else {
      valueToEmit = this.selected ? this.selected.value : undefined;
    }
    this._value = valueToEmit;
    this.change.emit(new ChipOptionGroupChangeEvent(this, valueToEmit));
    this.onChange(valueToEmit);
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Deselects every chip in the option group.
   * @param skip Chip that should not be deselected.
   */
  private clearSelection(skip?: ChipBaseComponent): void {
    this.chips.forEach((chip) => {
      if (chip !== skip) {
        chip.deselect();
      }
    });
  }

  /**
   * Finds and selects the chip based on its value.
   * @returns Chip that has the corresponding value.
   */
  private _selectValue(value: any, isUserInput: boolean): ChipBaseComponent | undefined {
    const correspondingChip = this.chips.find((chip) => chip.value != null && this.compareWith(chip.value, value));

    if (correspondingChip) {
      if (isUserInput) {
        correspondingChip.selectViaInteraction();
      } else {
        correspondingChip.select();
      }
    }

    return correspondingChip;
  }

  /**
   * Syncs the chip-option-group selection state with the individual chips
   */
  private syncOptionGroupProperties(): void {
    if (this.chips) {
      // Defer setting the value in order to avoid the "Expression
      // has changed after it was checked" errors from Angular.
      Promise.resolve().then(() => {
        this.chips.forEach((chip) => {
          chip.chipListMultiple = this.multiple;
          chip.chipListSelectable = this._selectable;
          chip.changeDetectorRef.markForCheck();
        });
      });
    }
  }

  /**
   * Returns the first selected chip in this option group, or undefined if no chips are selected
   */
  private getFirstSelectedChip(): ChipOptionComponent | undefined {
    if (Array.isArray(this.selected)) {
      return this.selected.length ? this.selected[0] : undefined;
    }

    return this.selected;
  }

  /**
   * Determines if key manager should avoid putting a given chip action in the tab index. Skip
   * non-interactive actions since the user can't do anything with them.
   */
  protected override skipPredicate(action: ChipActionDirective): boolean {
    // Override the skip predicate in the base class to avoid skipping disabled chips. Allow
    // disabled chip options to receive focus to align with WAI ARIA recommendation. Normally WAI
    // ARIA's instructions are to exclude disabled items from the tab order, but it makes a few
    // exceptions for compound widgets.
    //
    // From [Developing a Keyboard Interface](
    // https://www.w3.org/WAI/ARIA/apg/practices/keyboard-interface/):
    //   "For the following composite widget elements, keep them focusable when disabled: Options in a
    //   option group..."
    return !action.isInteractive;
  }
}
